import { z } from 'zod'

/**
 * 電子カルテフォームのバリデーションスキーマ
 */

// 性別
export const genderSchema = z.enum(['男性', '女性', 'その他'], {
  errorMap: () => ({ message: '性別を選択してください' }),
})

// 来店きっかけ
export const visitSourceSchema = z.enum(
  ['Hotpepper', 'Web検索', 'Instagram', 'X', '地元情報誌', '口コミ', 'その他'],
  {
    errorMap: () => ({ message: '来店きっかけを選択してください' }),
  }
)

// 職業
export const occupationSchema = z
  .enum(['会社員', '自営業', '主婦', '学生', 'その他'])
  .or(z.literal(''))
  .transform((val) => (val === '' ? undefined : val))
  .optional()

// 電話番号（ハイフンなし10-11桁）
export const phoneNumberSchema = z
  .string()
  .min(1, '電話番号を入力してください')
  .regex(/^0\d{9,10}$/, '有効な電話番号を入力してください（ハイフンなし）')

// メールアドレス
export const emailSchema = z
  .string()
  .email('有効なメールアドレスを入力してください')
  .optional()
  .or(z.literal(''))

// カナ（全角カタカナのみ）
export const kanaSchema = z
  .string()
  .min(1, 'カナを入力してください')
  .regex(/^[ァ-ヶー]+$/, '全角カタカナで入力してください')

// 生年月日
export const birthdaySchema = z
  .string()
  .min(1, '生年月日を入力してください')
  .regex(/^\d{4}-\d{2}-\d{2}$/, '有効な日付を入力してください（YYYY-MM-DD形式）')
  .refine((date) => {
    const parsedDate = new Date(date)
    return !isNaN(parsedDate.getTime())
  }, '有効な日付を入力してください')
  .refine((date) => {
    const year = parseInt(date.split('-')[0])
    return year >= 1900 && year <= new Date().getFullYear()
  }, '1900年から今年の範囲で入力してください')
  .refine((date) => {
    const parsedDate = new Date(date)
    const today = new Date()
    return parsedDate <= today
  }, '未来の日付は入力できません')

/**
 * Step 1: 基本情報
 */
export const basicInfoSchema = z.object({
  lastNameKanji: z.string().min(1, '姓を入力してください'),
  firstNameKanji: z.string().min(1, '名を入力してください'),
  lastNameKana: kanaSchema,
  firstNameKana: kanaSchema,
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
  gender: genderSchema,
  birthday: birthdaySchema,
})

/**
 * Step 2: 詳細情報
 */
export const detailsSchema = z.object({
  visitSource: visitSourceSchema,
  occupation: occupationSchema,
})

// 服のサイズ
export const clothingSizeSchema = z.enum(['S', 'M', 'L', 'LL', '3L'], {
  errorMap: () => ({ message: '服のサイズを選択してください' }),
})

// リラクゼーション経験
export const relaxationExperienceSchema = z.enum(['有', '無'], {
  errorMap: () => ({ message: 'リラクゼーション経験を選択してください' }),
})

/**
 * Step 3: 施術情報
 */
export const memoSchema = z.object({
  clothingSize: clothingSizeSchema,
  relaxationExperience: relaxationExperienceSchema,
  chiefComplaint: z.string().min(1, '主訴（気になる症状）を入力してください'),
})

/**
 * Step 4: 同意情報
 */
export const consentSchema = z.object({
  agreedToTerms: z.literal(true, {
    errorMap: () => ({ message: '同意が必要です' }),
  }),
})

/**
 * 完全なフォームスキーマ
 */
export const karteFormSchema = z.object({
  basicInfo: basicInfoSchema,
  details: detailsSchema,
  memo: memoSchema,
  consent: consentSchema,
})

// 型推論用のエクスポート
export type BasicInfoInput = z.infer<typeof basicInfoSchema>
export type DetailsInput = z.infer<typeof detailsSchema>
export type MemoInput = z.infer<typeof memoSchema>
export type ConsentInput = z.infer<typeof consentSchema>
export type KarteFormInput = z.infer<typeof karteFormSchema>
